import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton, Typography } from "@mui/material";

import { colors } from "../../../theme/palette";
import {
  ArrowBackIcon,
  CreateNowIcon,
  FootballMatchIcon,
  TrainingIcon,
  TryoutIcon,
} from "../../../assets/icons/MUI/EventsIcons";
import APP_ROUTES from "../../../routers/routeConstants";

import CustomizedSteppers from "./CustomStepper";
import RulesPopUp from "./RulesPopUp";

const CreateEvent = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const [eventType, setEventType] = useState<"training" | "friendly-match" | "tryout">("training");
  const [openRulesDialog, setOpenRulesDialog] = useState(false);

  return (
    <Box
      sx={{
        padding: { xs: "30px 16px 16px", sm: "90px 50px 75px" },
        background: colors.White,
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        width: "100%",
        overflow: "hidden",
        position: "relative",
        textAlign: "center",
      }}
    >
      <IconButton
        onClick={() => navigate(APP_ROUTES.EVENTS)}
        sx={{
          position: "absolute",
          top: "25px",
          left: "16px",
          transform: currentLang === "ar" ? "rotate(180deg)" : "unset",
        }}
      >
        <ArrowBackIcon />
      </IconButton>
      <Typography
        variant="h1ExtraBold"
        sx={{
          color: "#1D1D1D",
        }}
      >
        {t("pages.events.createEvent.title")}
      </Typography>
      <Typography
        variant="body16Regular"
        sx={{
          color: "#232323",
          marginBottom: "28px",
          display: "block",
        }}
      >
        {t("pages.events.createEvent.chooseEventType")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: "16px",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "55px",
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", sm: "200px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            textAlign: "start",
            rowGap: "20px",
            padding: "16px",
            borderRadius: "16px",
            border: "1px solid #EAECF0",
          }}
        >
          <FootballMatchIcon />
          <Typography
            variant="h4ExtraBold"
            sx={{
              color: colors.Black100,
            }}
          >
            {t("pages.events.createEvent.friendlyMatch")}
          </Typography>
          <Typography
            variant="body13Regular"
            sx={{
              color: colors.Gray400,
              minHeight: "48px",
            }}
          >
            {t("pages.events.createEvent.friendlyMatchDescription")}
          </Typography>
          <Button
            variant="text-lg"
            endIcon={<CreateNowIcon />}
            onClick={() => {
              setOpenRulesDialog(true);
              setEventType("friendly-match");
            }}
            sx={{
              background: "transparent !important",
              paddingInlineStart: "0",
              justifyContent: "start",
              "& .MuiButton-icon": {
                transform: currentLang === "ar" ? "scaleX(-1)" : "",
              },
            }}
          >
            {t("pages.events.createEvent.createNow")}
          </Button>
        </Box>
        <Box
          sx={{
            width: { xs: "100%", sm: "200px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            textAlign: "start",
            rowGap: "20px",
            padding: "16px",
            borderRadius: "16px",
            border: "1px solid #EAECF0",
          }}
        >
          <TrainingIcon />
          <Typography
            variant="h4ExtraBold"
            sx={{
              color: colors.Black100,
            }}
          >
            {t("pages.events.createEvent.training")}
          </Typography>
          <Typography
            variant="body13Regular"
            sx={{
              color: colors.Gray400,
              minHeight: "48px",
            }}
          >
            {t("pages.events.createEvent.trainingDescription")}
          </Typography>
          <Button
            variant="text-lg"
            endIcon={<CreateNowIcon />}
            onClick={() => {
              setOpenRulesDialog(true);
              setEventType("training");
            }}
            sx={{
              background: "transparent !important",
              paddingInlineStart: "0",
              justifyContent: "start",
              "& .MuiButton-icon": {
                transform: currentLang === "ar" ? "scaleX(-1)" : "",
              },
            }}
          >
            {t("pages.events.createEvent.createNow")}
          </Button>
        </Box>
        <Box
          sx={{
            width: { xs: "100%", sm: "200px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            textAlign: "start",
            rowGap: "20px",
            padding: "16px",
            borderRadius: "16px",
            border: "1px solid #EAECF0",
          }}
        >
          <TryoutIcon />
          <Typography
            variant="h4ExtraBold"
            sx={{
              color: colors.Black100,
            }}
          >
            {t("pages.events.createEvent.tryout")}
          </Typography>
          <Typography
            variant="body13Regular"
            sx={{
              color: colors.Gray400,
              minHeight: "48px",
            }}
          >
            {t("pages.events.createEvent.tryoutDescription")}
          </Typography>
          <Button
            variant="text-lg"
            endIcon={<CreateNowIcon />}
            onClick={() => {
              setOpenRulesDialog(true);
              setEventType("tryout");
            }}
            sx={{
              background: "transparent !important",
              paddingInlineStart: "0",
              justifyContent: "start",
              "& .MuiButton-icon": {
                transform: currentLang === "ar" ? "scaleX(-1)" : "",
              },
            }}
          >
            {t("pages.events.createEvent.createNow")}
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          padding: "26px 32px 56px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "16px",
          background: colors.Gray50,
        }}
      >
        <Typography
          variant="h4ExtraBold"
          sx={{
            color: colors.Dark1000,
            marginBottom: "24px",
            display: "block",
          }}
        >
          {t("pages.events.createEvent.howItWorks")}
        </Typography>
        <Typography
          variant="body16Bold"
          sx={{
            color: colors.Black100,
            marginBottom: "8px",
            display: "block",
          }}
        >
          {t("pages.events.createEvent.howItWorksDescription")}
        </Typography>
        <Typography
          variant="body13Regular"
          sx={{
            color: colors.Dark600,
            marginBottom: "70px",
            display: "block",
            maxWidth: "382px",
          }}
        >
          {t("pages.events.createEvent.effortlessScheduling")}
        </Typography>
        <CustomizedSteppers />
      </Box>
      <RulesPopUp open={openRulesDialog} handleClose={() => setOpenRulesDialog(false)} eventType={eventType} />
    </Box>
  );
};

export default CreateEvent;
